import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { selectActiveNonTaxonomyFacets } from 'src/redux/productsQuery/selectors/selectors';
import { DELIVERY_DAYS_FACET_KEY } from 'src/utils/deliveryChipDates';
import { GUARANTEED_ARRIVAL_DAYS } from 'src/utils/guaranteedArrivalDays';

// GA4 does not log a parameter value longer than this, so a long set is cut rather than lost.
const GA4_PARAMETER_VALUE_LIMIT = 100;
const SEPARATOR = '|';
const TRUNCATED_MARKER = '…';

const byKey =
  <T>(key: (item: T) => string) =>
  (a: T, b: T) =>
    key(a).localeCompare(key(b));

const joinWithinLimit = (pairs: string[]): string => {
  const joined = pairs.join(SEPARATOR);

  if (joined.length <= GA4_PARAMETER_VALUE_LIMIT) {
    return joined;
  }

  const room = GA4_PARAMETER_VALUE_LIMIT - TRUNCATED_MARKER.length;
  let kept = '';

  for (const pair of pairs) {
    const next = `${kept}${pair}${SEPARATOR}`;

    if (next.length > room) {
      break;
    }

    kept = next;
  }

  return `${kept || joined.slice(0, room)}${TRUNCATED_MARKER}`;
};

/**
 * Every filter the customer has applied, as `Facet: Option` pairs joined with `|`, for
 * the `applied_filter_values` dimension. Unlike `filters_applied`, which lists facet
 * titles only, this is enough to tell which combinations empty a page.
 *
 * Pairs are ordered by facet key then option value, because the response decides facet
 * order and the same set must always read the same. A set too long for GA4 is cut at a
 * whole pair and ends with `…`.
 *
 * The guaranteed arrival day is written as an option of the delivery facet, matching
 * how the toggle is reported on `select_filter`, because no facet of its own holds it.
 */
export const getAppliedFilterValues = (state: ReduxApplicationState): string => {
  const day = state.productsQuery.guaranteedArrivalDay;

  const pairs = [...selectActiveNonTaxonomyFacets(state)]
    .sort(byKey((facet) => facet.key))
    .flatMap((facet) => {
      const values = facet.options
        .filter((option) => option.active)
        .sort(byKey((option) => option.value))
        .map((option) => `${facet.title}: ${option.title}`);

      if (day && facet.key === DELIVERY_DAYS_FACET_KEY) {
        values.push(`${facet.title}: Guaranteed ${GUARANTEED_ARRIVAL_DAYS[day].name}`);
      }

      return values;
    });

  return joinWithinLimit(pairs);
};
